
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Save, Edit, UserCheck } from "lucide-react";

const MemberTieringTab = () => {
  const { toast } = useToast();
  
  // Tier configuration state
  const [tiers, setTiers] = useState([
    {
      id: 1,
      name: "Basic",
      color: "gray",
      minPoints: 0,
      maxPoints: 999,
      benefits: {
        discountPercent: 0,
        earlyAccess: false,
        birthdayVoucher: 0,
        freeShipping: false
      },
      isEditing: false
    },
    {
      id: 2,
      name: "Silver",
      color: "silver",
      minPoints: 1000,
      maxPoints: 4999,
      benefits: {
        discountPercent: 5,
        earlyAccess: true,
        birthdayVoucher: 10,
        freeShipping: false
      },
      isEditing: false
    },
    {
      id: 3,
      name: "Gold",
      color: "gold",
      minPoints: 5000,
      maxPoints: null,
      benefits: {
        discountPercent: 10,
        earlyAccess: true,
        birthdayVoucher: 25,
        freeShipping: true
      },
      isEditing: false
    }
  ]);
  
  // Tier retention settings
  const [retentionSettings, setRetentionSettings] = useState({
    reviewFrequency: "monthly",
    downgradePeriod: 3 // months
  });
  
  // Toggle edit mode for a tier
  const toggleEditMode = (tierId: number) => {
    setTiers(
      tiers.map(tier => {
        if (tier.id === tierId) {
          return { ...tier, isEditing: !tier.isEditing };
        }
        return tier;
      })
    );
  };
  
  // Update tier name
  const updateTierName = (tierId: number, newName: string) => {
    setTiers(
      tiers.map(tier => {
        if (tier.id === tierId) {
          return { ...tier, name: newName };
        }
        return tier;
      })
    );
  };
  
  // Update tier threshold
  const updateTierThreshold = (tierId: number, field: 'minPoints' | 'maxPoints', value: number | null) => {
    setTiers(
      tiers.map(tier => {
        if (tier.id === tierId) {
          return { ...tier, [field]: value };
        }
        return tier;
      })
    );
  };
  
  // Update tier benefit
  const updateTierBenefit = (tierId: number, benefit: string, value: number | boolean) => {
    setTiers(
      tiers.map(tier => {
        if (tier.id === tierId) {
          return {
            ...tier,
            benefits: {
              ...tier.benefits,
              [benefit]: value
            }
          };
        }
        return tier;
      })
    );
  };
  
  // Save all tier settings
  const saveTierSettings = () => {
    // Here we would save to IndexedDB in a real app
    console.log("Saving tier settings:", tiers);
    
    // Reset all editing states
    setTiers(
      tiers.map(tier => ({ ...tier, isEditing: false }))
    );
    
    toast({
      title: "Tier Settings Saved",
      description: "Member tier configuration has been updated."
    });
  };
  
  // Save retention settings
  const saveRetentionSettings = () => {
    // Here we would save to IndexedDB in a real app
    console.log("Saving retention settings:", retentionSettings);
    
    toast({
      title: "Retention Settings Saved",
      description: "Member tier retention rules have been updated."
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Member Tiering</h2>
      
      <Tabs defaultValue="tiers" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tiers">Tier Levels & Benefits</TabsTrigger>
          <TabsTrigger value="retention">Upgrade & Retention Rules</TabsTrigger>
        </TabsList>
        
        {/* Tier Levels Tab */}
        <TabsContent value="tiers" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">
              Configure membership tiers, point thresholds, and tier-specific benefits
            </p>
            <Button onClick={saveTierSettings}>
              <Save className="h-4 w-4 mr-2" /> Save Tier Configuration
            </Button>
          </div>
          
          {tiers.map((tier) => (
            <Card key={tier.id} className={`border-l-4 ${
              tier.color === "gold" ? "border-l-yellow-400" :
              tier.color === "silver" ? "border-l-gray-400" :
              "border-l-gray-300"
            }`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center">
                  <Badge variant={
                    tier.color === "gold" ? "default" :
                    tier.color === "silver" ? "secondary" :
                    "outline"
                  } className="mr-2">
                    {tier.name}
                  </Badge>
                  
                  {tier.isEditing ? (
                    <Input
                      value={tier.name}
                      onChange={(e) => updateTierName(tier.id, e.target.value)}
                      className="w-40 ml-2"
                    />
                  ) : (
                    <CardTitle>{tier.name} Tier</CardTitle>
                  )}
                </div>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => toggleEditMode(tier.id)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {tier.isEditing ? "Done" : "Edit"}
                </Button>
              </CardHeader>
              
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Points Threshold */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Points Threshold</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`min-points-${tier.id}`}>Minimum Points</Label>
                        <Input
                          id={`min-points-${tier.id}`}
                          type="number"
                          value={tier.minPoints}
                          onChange={(e) => updateTierThreshold(tier.id, 'minPoints', parseInt(e.target.value))}
                          disabled={!tier.isEditing}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor={`max-points-${tier.id}`}>Maximum Points</Label>
                        <Input
                          id={`max-points-${tier.id}`}
                          type="number"
                          value={tier.maxPoints ?? ''}
                          placeholder="No upper limit"
                          onChange={(e) => updateTierThreshold(
                            tier.id, 
                            'maxPoints', 
                            e.target.value === '' ? null : parseInt(e.target.value)
                          )}
                          disabled={!tier.isEditing}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Tier Benefits */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Tier Benefits</h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`discount-${tier.id}`}>Extra Discount %</Label>
                        <Input
                          id={`discount-${tier.id}`}
                          type="number"
                          value={tier.benefits.discountPercent}
                          onChange={(e) => updateTierBenefit(tier.id, 'discountPercent', parseInt(e.target.value))}
                          disabled={!tier.isEditing}
                          className="w-20"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`early-access-${tier.id}`}>Early Access to Promotions</Label>
                        <Switch
                          id={`early-access-${tier.id}`}
                          checked={tier.benefits.earlyAccess}
                          onCheckedChange={(checked) => updateTierBenefit(tier.id, 'earlyAccess', checked)}
                          disabled={!tier.isEditing}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`birthday-voucher-${tier.id}`}>Birthday Gift Voucher (RM)</Label>
                        <Input
                          id={`birthday-voucher-${tier.id}`}
                          type="number"
                          value={tier.benefits.birthdayVoucher}
                          onChange={(e) => updateTierBenefit(tier.id, 'birthdayVoucher', parseInt(e.target.value))}
                          disabled={!tier.isEditing}
                          className="w-20"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`free-shipping-${tier.id}`}>Free Shipping</Label>
                        <Switch
                          id={`free-shipping-${tier.id}`}
                          checked={tier.benefits.freeShipping}
                          onCheckedChange={(checked) => updateTierBenefit(tier.id, 'freeShipping', checked)}
                          disabled={!tier.isEditing}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        
        {/* Retention Rules Tab */}
        <TabsContent value="retention">
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Tier Upgrade & Retention Logic</CardTitle>
              <CardDescription>
                Configure how members move between tiers and retention requirements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="review-frequency">Tier Review Frequency</Label>
                  <Select
                    value={retentionSettings.reviewFrequency}
                    onValueChange={(value) => setRetentionSettings({
                      ...retentionSettings,
                      reviewFrequency: value
                    })}
                  >
                    <SelectTrigger id="review-frequency">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="biannually">Bi-annually</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="downgrade-period">Downgrade If No Activity After</Label>
                  <div className="flex items-center">
                    <Input
                      id="downgrade-period"
                      type="number"
                      value={retentionSettings.downgradePeriod}
                      onChange={(e) => setRetentionSettings({
                        ...retentionSettings,
                        downgradePeriod: parseInt(e.target.value)
                      })}
                      className="w-20"
                    />
                    <span className="ml-2 text-muted-foreground">months</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Members will be downgraded one tier level if they have no point activity for this period
                  </p>
                </div>
              </div>
              
              <div className="p-4 border rounded-md bg-muted/20">
                <h3 className="font-medium mb-2 flex items-center">
                  <UserCheck className="h-4 w-4 mr-2" />
                  Automatic Tier Movement Rules
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Members automatically move up tiers when they reach the minimum points threshold</li>
                  <li>Points are calculated based on total current point balance, not historical earnings</li>
                  <li>Tier benefits are applied immediately upon tier change</li>
                  <li>Review process runs at the frequency configured above</li>
                  <li>Members will retain their points even if downgraded to a lower tier</li>
                </ul>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full" onClick={saveRetentionSettings}>
                <Save className="h-4 w-4 mr-2" /> Save Retention Settings
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MemberTieringTab;
